/**
 * FILE-OVERVIEW:
 * - 役割: S3上のXMLファイル一覧表示・ダウンロードUI。
 * - 主な連携: services/s3Service.js
 * - 本番運用上の注意: プレフィックスや並び順変更は運用手順に影響する。
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { listFiles, downloadFile, formatFileSize } from '../../services/s3Service';

const S3FileList = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // ファイル一覧を取得
  const fetchFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const fileList = await listFiles();
      setFiles(fileList);
    } catch (err) {
      setError(err.message || 'ファイル一覧の取得に失敗しました');
      console.error('ファイル取得エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  // ファイルをダウンロード
  const handleDownload = async (fileKey) => {
    setDownloading(fileKey);
    try {
      await downloadFile(fileKey);
    } catch (err) {
      setError(err.message || 'ファイルのダウンロードに失敗しました');
      console.error('ダウンロードエラー:', err);
    } finally {
      setDownloading(null);
    }
  };

  // 日時フォーマット
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 初回読み込み
  useEffect(() => {
    fetchFiles();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/memo')}
            sx={{ color: 'primary.main' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">S3ファイル一覧</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchFiles}
          disabled={loading}
        >
          更新
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : files.length === 0 ? (
        <Alert severity="info">ファイルがありません</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ファイル名</TableCell>
                <TableCell>サイズ</TableCell>
                <TableCell>最終更新日時</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.key} hover>
                  <TableCell>
                    <Typography variant="body2">{file.fileName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {file.key}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatFileSize(file.size)}</TableCell>
                  <TableCell>{formatDate(file.lastModified)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleDownload(file.key)}
                      disabled={downloading === file.key}
                    >
                      {downloading === file.key ? (
                        <CircularProgress size={24} />
                      ) : (
                        <DownloadIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default S3FileList;
